/**
 * Skeleton - Reusable loading skeleton components
 *
 * Provides shimmer animation placeholders for:
 * - Inbox thread rows
 * - Notification items
 * - Activity cards
 * - Message bubbles
 */

interface SkeletonProps {
  className?: string;
}

// Base skeleton with shimmer animation
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse bg-slate-700/50 rounded ${className}`}
    />
  );
}

// Skeleton for inbox thread row (avatar + text lines)
export function InboxThreadSkeleton() {
  return (
    <div className="flex items-center gap-4 px-4 py-4">
      {/* Avatar */}
      <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-2.5 w-12" />
      </div>
    </div>
  );
}

// Skeleton for inbox dropdown thread row (compact)
export function InboxDropdownSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      {/* Avatar */}
      <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <Skeleton className="h-3.5 w-20" />
        <Skeleton className="h-2.5 w-32" />
        <Skeleton className="h-2 w-10" />
      </div>
    </div>
  );
}

// Skeleton for notification item
export function NotificationSkeleton() {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      {/* Avatar */}
      <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <Skeleton className="h-3.5 w-36" />
        <Skeleton className="h-2.5 w-full max-w-[200px]" />
        <Skeleton className="h-2 w-14" />
      </div>
    </div>
  );
}

// Skeleton for activity card in social feed
export function ActivityCardSkeleton() {
  return (
    <div className="bg-[#1A1F26]/30 rounded-xl p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Skeleton className="w-8 h-8 rounded-full" />
        <div className="space-y-1.5">
          <Skeleton className="h-3 w-20" />
        </div>
        <Skeleton className="h-2 w-8 ml-auto" />
      </div>

      {/* Action text */}
      <Skeleton className="h-3 w-44" />

      {/* Media card */}
      <div className="flex gap-3 p-2 rounded-lg">
        <Skeleton className="w-[60px] h-[90px] rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2 py-1">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-2.5 w-20" />
        </div>
      </div>
    </div>
  );
}

// Skeleton for follower/following row (avatar + name + follow button)
export function FollowerRowSkeleton() {
  return (
    <div className="flex items-center gap-3 p-3 bg-[#1A1F26]/30 rounded-lg">
      {/* Avatar */}
      <Skeleton className="w-12 h-12 rounded-full flex-shrink-0" />

      {/* Name lines */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-20" />
      </div>

      {/* Follow button */}
      <Skeleton className="h-8 w-[88px] rounded-lg" />
    </div>
  );
}

// Skeleton for message bubble in conversation
export function MessageBubbleSkeleton({ isOutgoing = false }: { isOutgoing?: boolean }) {
  return (
    <div className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] ${
          isOutgoing
            ? 'bg-orange-500/10 rounded-2xl rounded-br-md'
            : 'bg-[#1A1F26]/50 rounded-2xl rounded-bl-md'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.04]">
          <Skeleton className="w-3 h-3 rounded-full" />
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2 w-10 ml-auto" />
        </div>

        {/* Content */}
        <div className="flex gap-3 p-3">
          <Skeleton className="w-12 h-18 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-2.5 w-36" />
          </div>
        </div>
      </div>
    </div>
  );
}
